import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardGroup,
  Col,
  Container,
  Row,
  Badge,
} from "reactstrap";
import { getAPIURL } from "../../config";
import history from "../../history";
import { store } from "../../State/store/configureStore";
import {
  LOG_IN_SUCCESS,
  SET_GLOBAL_STORAGE,
  UPDATE_PREFERRED_LOCALE,
} from "../../State/actions/actionTypes";
import { http } from "../../utils/http";
import userService from "../../services/userService";
import { devLog } from "../../utils/devLog";
import Text from "../../components/Text";
import Messages from "../../Shared/Messages";

const EntraIdFacilitySelector = () => {
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState(null);
  const [preAuthToken, setPreAuthToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Get facility selection data from sessionStorage (set by EntraIdCallback)
    const selectionData = sessionStorage.getItem("entraIdFacilitySelection");

    if (!selectionData) {
      devLog("No facility selection data found");
      setErrorKey("entra_session_expired");
      setTimeout(() => {
        history.push("/login");
      }, 2000);
      return;
    }

    try {
      const data = JSON.parse(selectionData);
      setFacilities(data.availableFacilities || []);
      setPreAuthToken(data.preAuthToken);
      setUser(data.user);

      // Auto-select if only one facility (shouldn't happen, but just in case)
      if (data.availableFacilities && data.availableFacilities.length === 1) {
        setSelectedFacilityId(data.availableFacilities[0].spaceId);
      }
    } catch (err) {
      devLog("Error parsing facility selection data:", err);
      setErrorKey("entra_invalid_session");
      setTimeout(() => {
        history.push("/login");
      }, 2000);
    }
  }, []);

  const handleFacilitySelect = async () => {
    if (!selectedFacilityId) {
      setErrorKey("entra_please_select_facility");
      return;
    }

    setLoading(true);
    setErrorKey(null);

    try {
      devLog("Selecting facility:", selectedFacilityId);

      const response = await fetch(
        `${getAPIURL()}/api/auth/entra/select-facility`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${preAuthToken}`,
          },
          body: JSON.stringify({
            spaceId: selectedFacilityId,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to select facility");
      }

      const data = await response.json();

      devLog("Facility selected successfully:", {
        hasAccessToken: !!data.access_token,
        tokenLength: data.access_token?.length,
      });

      // Set the token in HTTP service
      http.setToken(data.access_token);

      // Get user/space info (updateStore=true to dispatch UPDATE_SPACE_INFO)
      devLog("Calling getSpaceInfo...");
      const userInfo = await userService.getSpaceInfo(true);
      devLog("getSpaceInfo completed");

      // Dispatch LOGIN_SUCCESS to persist authentication
      // Must include tokenTimestamp so REHYDRATE can restore the session on refresh
      const userInfoWithoutUIFields = {
        ...userInfo,
        attributeUIFields: undefined,
        authMethod: "entra_id",
      };

      store.dispatch({
        type: LOG_IN_SUCCESS,
        data: userInfoWithoutUIFields,
        token: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
        tokenTimestamp: Date.now(),
      });

      // Dispatch global storage and locale — same as normal login saga
      store.dispatch({
        type: SET_GLOBAL_STORAGE,
        data: userInfo.attributeUIFields,
      });

      store.dispatch({
        type: UPDATE_PREFERRED_LOCALE,
        data: {
          preferredLocale:
            localStorage.getItem("preferredLocale") || userInfo.defaultLocale,
        },
      });

      devLog(
        "Dispatched LOG_IN_SUCCESS, SET_GLOBAL_STORAGE, UPDATE_PREFERRED_LOCALE",
      );

      // Clear session storage
      sessionStorage.removeItem("entraIdFacilitySelection");

      // Small delay to ensure Redux store is updated
      setTimeout(() => {
        history.push("/");
      }, 100);
    } catch (err) {
      devLog("Error selecting facility:", err);
      setErrorKey("entra_failed_select_facility");
      setLoading(false);
    }
  };

  if (errorKey && !facilities.length) {
    return (
      <div
        className="app flex-column justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md="6" sm={12}>
              <div className="text-center">
                <i
                  className="fa fa-exclamation-circle"
                  style={{
                    fontSize: "48px",
                    color: "#f86c6b",
                    marginBottom: "20px",
                  }}
                ></i>
                <h3 style={{ color: "#23282c", fontWeight: 500 }}>
                  <Text content={Messages.entra_session_error} />
                </h3>
                <p style={{ color: "#73818f", marginTop: "10px" }}>
                  <Text content={Messages[errorKey]} />
                </p>
                <p
                  style={{
                    color: "#73818f",
                    fontSize: "14px",
                    marginTop: "10px",
                  }}
                >
                  <Text content={Messages.entra_redirecting_to_login} />
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="app flex-column justify-content-center align-items-center">
      <Container>
        <Row className="justify-content-center">
          <Col md="6" sm={12}>
            <div className="ob-logos mb-3 justify-content-center my-4">
              <div>
                <img
                  alt="myPatientSpace"
                  src="/img/logo-symbol.png"
                  height={32}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <Container>
        <Row className="justify-content-center">
          <Col md="6" sm={12}>
            <CardGroup>
              <Card>
                <CardBody>
                  <div className="text-center mb-3">
                    <h4 className="mb-2">
                      <Text content={Messages.entra_select_your_facility} />
                    </h4>
                    {user && (
                      <p className="text-muted">
                        <Text
                          content={Messages.entra_welcome_user}
                          data={{ user: user.email || user.loginUserId }}
                        />
                      </p>
                    )}
                    <p className="text-muted small">
                      <Text content={Messages.entra_multiple_facilities_msg} />
                    </p>
                  </div>

                  {errorKey && (
                    <div className="alert alert-danger" role="alert">
                      <Text content={Messages[errorKey]} />
                    </div>
                  )}

                  <div className="facility-list mb-4">
                    {facilities.map((facility) => (
                      <div
                        key={facility.spaceId}
                        className={`facility-option p-3 mb-2 ${
                          selectedFacilityId === facility.spaceId
                            ? "border-primary"
                            : "border-secondary"
                        }`}
                        style={{
                          border: "2px solid",
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          backgroundColor:
                            selectedFacilityId === facility.spaceId
                              ? "#e7f3ff"
                              : "transparent",
                        }}
                        onClick={() => setSelectedFacilityId(facility.spaceId)}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center">
                              <input
                                type="radio"
                                name="facility"
                                value={facility.spaceId}
                                checked={
                                  selectedFacilityId === facility.spaceId
                                }
                                onChange={() =>
                                  setSelectedFacilityId(facility.spaceId)
                                }
                                className="mr-3"
                                style={{ cursor: "pointer" }}
                              />
                              <div>
                                <h5
                                  className="mb-1"
                                  style={{ fontWeight: 500 }}
                                >
                                  {facility.facilityName || (
                                    <Text
                                      content={Messages.entra_unknown_facility}
                                    />
                                  )}
                                </h5>
                                {facility.role && (
                                  <div className="mt-1">
                                    <small className="text-muted mr-1">
                                      Your role:
                                    </small>
                                    <Badge color="info">
                                      {facility.role
                                        .replace("ROLE_", "")
                                        .replace(/_/g, " ")}
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="d-flex justify-content-between">
                    <Button
                      color="secondary"
                      onClick={() => {
                        sessionStorage.removeItem("entraIdFacilitySelection");
                        history.push("/login");
                      }}
                      disabled={loading}
                    >
                      <Text content={Messages.cancel} />
                    </Button>
                    <Button
                      color="primary"
                      onClick={handleFacilitySelect}
                      disabled={!selectedFacilityId || loading}
                      style={{ minWidth: "120px" }}
                    >
                      {loading ? (
                        <>
                          <i className="fa fa-spinner fa-spin mr-2"></i>
                          <Text content={Messages.entra_signing_in} />
                        </>
                      ) : (
                        <Text content={Messages.continue} />
                      )}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </CardGroup>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EntraIdFacilitySelector;
